/**
 * Wealth Scoring Engine
 * ---------------------
 * Calcule le score patrimonial global d'un utilisateur sur 100 points,
 * décomposé en 6 dimensions pondérées :
 *
 *   • Épargne de précaution      (20%)
 *   • Diversification            (20%)
 *   • Endettement                (15%)
 *   • Prévoyance & retraite      (20%)
 *   • Optimisation fiscale       (15%)
 *   • Pilotage par objectifs     (10%)
 *
 * L'engine est volontairement déterministe et explicable :
 * chaque sous-score est justifié par une règle métier que
 * l'on peut afficher à l'utilisateur ("pourquoi cette note ?").
 */

export const DIMENSION_WEIGHTS = {
  epargne: 0.2,
  diversification: 0.2,
  endettement: 0.15,
  prevoyance: 0.2,
  fiscalite: 0.15,
  objectifs: 0.1,
}

/**
 * Convertit un score /100 en lettre A+ → F.
 */
export function scoreToGrade(score) {
  if (score >= 90) return 'A+'
  if (score >= 80) return 'A'
  if (score >= 70) return 'B'
  if (score >= 55) return 'C'
  if (score >= 40) return 'D'
  if (score >= 25) return 'E'
  return 'F'
}

/**
 * Couleur associée au score (cohérente avec la palette dashboard).
 */
export function scoreToColor(score) {
  if (score >= 75) return '#22c55e' // green-500
  if (score >= 55) return '#84cc16' // lime-500
  if (score >= 40) return '#f59e0b' // amber-500
  if (score >= 25) return '#f97316' // orange-500
  return '#ef4444' // red-500
}

/**
 * Score d'épargne de précaution.
 * Cible : 3 à 6 mois de dépenses disponibles immédiatement.
 */
export function scoreEpargnePrecaution({ liquiditesDisponibles, depensesMensuelles }) {
  if (depensesMensuelles <= 0) return 50
  const mois = liquiditesDisponibles / depensesMensuelles
  if (mois >= 6) return 100
  if (mois >= 3) return 75
  if (mois >= 1) return 40
  if (mois >= 0.5) return 20
  return 5
}

/**
 * Score de diversification.
 * Calcule un indice de concentration de Herfindahl inversé.
 */
export function scoreDiversification(repartition) {
  // repartition : { compte_courant: 0.4, livret_a: 0.1, av: 0.2, pea: 0.2, immo: 0.1 }
  const hhi = Object.values(repartition).reduce((acc, v) => acc + v * v, 0)
  // hhi va de 1/n (parfaitement diversifié) à 1 (tout concentré)
  // on transforme en score 0-100
  return Math.round((1 - hhi) * 100)
}

/**
 * Score d'endettement.
 * Pénalise un patrimoine net négatif ou un taux d'endettement > 35%.
 */
export function scoreEndettement({ patrimoineNet, revenuMensuel, mensualitesCredits = 0 }) {
  let s = 100
  if (patrimoineNet < 0) s -= 40
  const tauxEndettement = revenuMensuel ? mensualitesCredits / revenuMensuel : 0
  if (tauxEndettement > 0.35) s -= 30
  else if (tauxEndettement > 0.25) s -= 10
  return Math.max(0, s)
}

/**
 * Score prévoyance/retraite.
 * Bonus si PER, AV de plus de 8 ans, prévoyance individuelle.
 */
export function scorePrevoyance({ aPer, anciennetéAv, aPrevoyance, age }) {
  let s = 0
  if (aPer) s += 40
  if (anciennetéAv >= 8) s += 30
  else if (anciennetéAv >= 1) s += 15
  if (aPrevoyance) s += 20
  // Bonus jeunesse : plus tôt on commence, plus le score est généreux à équivalent contributions
  if (age && age < 35 && (aPer || anciennetéAv >= 1)) s += 10
  return Math.min(100, s)
}

/**
 * Score d'optimisation fiscale.
 * Évalue si l'utilisateur exploite les niches disponibles à sa TMI.
 */
export function scoreFiscalite({ tmi, dispositifsActifs = [] }) {
  if (tmi < 0.11) return 80 // pas vraiment optimisable
  let s = 20
  if (dispositifsActifs.includes('per')) s += 30
  if (dispositifsActifs.includes('pee')) s += 15
  if (dispositifsActifs.includes('pinel') || dispositifsActifs.includes('lmnp')) s += 20
  if (dispositifsActifs.includes('don_association')) s += 5
  if (dispositifsActifs.includes('fcpi_fip')) s += 10
  return Math.min(100, s)
}

/**
 * Score de pilotage par objectifs.
 * Encourage la définition d'objectifs alignés avec la règle 40-10-15-15-20.
 */
export function scoreObjectifs({ objectifsDefinis, repartitionVsCible }) {
  if (!objectifsDefinis || objectifsDefinis.length === 0) return 0
  let s = 30 + objectifsDefinis.length * 5
  // bonus si la répartition réelle suit la cible 40/10/15/15/20
  if (repartitionVsCible && repartitionVsCible < 0.15) s += 25
  return Math.min(100, s)
}

/**
 * Composé global pondéré.
 */
export function computeGlobalScore(dimensions) {
  return Math.round(
    dimensions.reduce((acc, d) => acc + d.score * (DIMENSION_WEIGHTS[d.key] || 0), 0),
  )
}

/**
 * Calcule l'impact attendu d'une liste d'actions sur le score global.
 * Renvoie un nouveau score projeté.
 */
export function projectScoreWithActions(currentDimensions, actions) {
  // Chaque action décrit un "scoreImpact" : +X points
  // On distribue cet impact sur la dimension concernée (cap à 100).
  const next = currentDimensions.map((d) => ({ ...d }))
  actions.forEach((a) => {
    const targetKey = mapCategoryToDimension(a.category)
    const target = next.find((d) => d.key === targetKey)
    if (!target) return
    const numericImpact = parseInt(String(a.scoreImpact).replace(/[^0-9-]/g, ''), 10) || 0
    target.score = Math.min(100, target.score + numericImpact)
  })
  return computeGlobalScore(next)
}

function mapCategoryToDimension(category) {
  const map = {
    'Épargne de précaution': 'epargne',
    'Optimisation fiscale': 'fiscalite',
    Diversification: 'diversification',
    Comportement: 'objectifs',
    'Patrimoine immobilier': 'diversification',
    'Marchés financiers': 'diversification',
    Prévoyance: 'prevoyance',
  }
  return map[category] || 'objectifs'
}
